import orderGiftCardPayment from 'api/definitions/orderGiftCardPayment';
import orderGiftCards from 'api/definitions/orderGiftCards';
import orderGiftCardsToken from 'api/definitions/orderGiftCardsToken';
import orderGiftCardsValidation from 'api/definitions/orderGiftCardsValidation';
import get from 'lodash/get';
import handleErrors from 'redux/errors/handle-errors';
import {
  ADD_GIFT_CARD,
  ADD_GIFT_CARD_PAYMENT,
  ADD_GIFT_CARD_TOKEN,
  ADD_GIFT_CARD_VALIDATION,
  ADD_GIFT_CARD_GET_ORDER,
} from 'redux/modules/order/actions/types';
import { getCurrentGiftCards } from 'redux/modules/order/selectors/get-current-gift-cards';
import { getGiftCardToken } from 'redux/modules/order/selectors/get-gift-card-token';
import { getGiftCardValidationNonce } from 'redux/modules/order/selectors/get-gift-card-validation-nonce';
import { getCustomerOrderId } from 'redux/modules/sessions/selectors';
import { encrypt } from 'utils/crypto/encrypt';
import { format } from 'utils/date';
import { ok, wrapTaggedApiError } from 'utils/result';
import order from 'api/definitions/order';
import { handleGiftCardError, handleTimeoutError } from './errors';

const handleResponse = dispatch => result => {
  const failures = get(result, 'data.setGiftCards.failures', null);
  if (failures) {
    const orderErrorResponseCode = failures.conflicts?.code;

    const errorResponse = {
      status: failures.statusCode,
      response: {
        body: {
          type: 'ADD_GIFT_CARD_ERROR',
          code: orderErrorResponseCode,
        },
      },
    };

    dispatch(handleErrors(errorResponse, [handleGiftCardError]));

    return (
      failures.statusCode === 400 ||
      [
        // error codes that will NOT reset the form
        'ORDER_PAYMENT_004',
        'ORDER_PAYMENT_029',
        'ORDER_PAYMENT_058',
        'ORDER_PAYMENT_076',
        'ORDER_PAYMENT_077',
        'ORDER_PAYMENT_093',
      ].indexOf(orderErrorResponseCode) === -1
    );
  }
  if (result?.errors) {
    const errorResponse = {
      response: {
        body: {
          type: 'ADD_GIFT_CARD_ERROR',
        },
      },
    };

    dispatch(handleErrors(errorResponse, [handleGiftCardError]));
    return true;
  }

  return true;
};

const handleRequestErrors = dispatch => error => {
  dispatch(handleErrors(error, [handleTimeoutError]));

  return true;
};

const addGiftCard =
  ({ serialNumber, giftCardPin }) =>
  (dispatch, getState) => {
    const state = getState();
    const customerOrderId = getCustomerOrderId(state);
    const currentGiftCards = getCurrentGiftCards(state);
    const giftCards = [...currentGiftCards, { serialNumber, giftCardPin }];

    return dispatch({
      apiCall: orderGiftCards.post({ customerOrderId, giftCards }),
      types: ADD_GIFT_CARD.triplet,
    }).then(handleResponse(dispatch), handleRequestErrors(dispatch));
  };

export const addOrderGiftCardV3 =
  ({ serialNumber, giftCardPin }) =>
  async (dispatch, getState) => {
    let state = getState();
    const customerOrderId = getCustomerOrderId(state);
    const taggedGiftCardsApiErrors = wrapTaggedApiError('giftCards');

    try {
      await dispatch({
        apiCall: orderGiftCardsToken.post({
          customerOrderId,
          iHandleStatusCodes: [404, 409, 500, 502],
        }),
        types: ADD_GIFT_CARD_TOKEN.triplet,
      });
    } catch (error) {
      return taggedGiftCardsApiErrors(error);
    }

    state = getState(); // Refresh state after the token request
    const giftCardToken = getGiftCardToken(state);
    if (!giftCardToken) {
      return taggedGiftCardsApiErrors(new Error('Gift card token not found'));
    }

    const { publicKey, salt, token } = giftCardToken;

    const currentTimestamp = format(new Date(), 'HHmmss');
    // Gifting Service `create-access-token` endpoint only accepts 9 characters maximum length
    // for the `clientReference` field, hence we decided to truncate the first character from it.
    // Since the `clientReference` value is actually the `customerOrderId` then the first character is not relevant.
    const clientReference = customerOrderId.slice(-9);
    const encryptedDetails = await dispatch(
      encrypt({
        value: `${serialNumber}:${giftCardPin}:${clientReference}:${salt}:${currentTimestamp}`,
        publicKey,
      }),
    );

    try {
      await dispatch({
        apiCall: orderGiftCardsValidation.post({
          body: { encryptedDetails },
          iHandleStatusCodes: true,
        }),
        types: ADD_GIFT_CARD_VALIDATION.triplet,
        jwt: `Bearer ${token}`,
      });
    } catch (error) {
      return taggedGiftCardsApiErrors(error);
    }

    state = getState(); // Refresh state after the token request
    const giftCardValidationNonce = getGiftCardValidationNonce(state);
    if (!giftCardValidationNonce) {
      return taggedGiftCardsApiErrors(new Error('Gift card validation nonce not found'));
    }

    try {
      await dispatch({
        apiCall: orderGiftCardPayment.post({
          customerOrderId,
          iHandleStatusCodes: [404, 409, 500, 502],
          body: { nonce: giftCardValidationNonce },
        }),
        types: ADD_GIFT_CARD_PAYMENT.triplet, // not storing success response in state
      });
    } catch (error) {
      return taggedGiftCardsApiErrors(error);
    }

    try {
      await dispatch({
        apiCall: order.get({ customerOrderId, iHandleStatusCodes: [404, 409, 500, 502] }),
        types: ADD_GIFT_CARD_GET_ORDER.triplet,
      });
    } catch (error) {
      return taggedGiftCardsApiErrors(error);
    }

    return ok(true);
  };

export default addGiftCard;
